import { z } from "zod";
import { groupImportModeSchema, groupLinkSchema } from "./club.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";
import { newFamilyChildSchema } from "./families.ts";
import { groupLinkStatusSchema, importClassificationSchema, syncRoleSchema } from "../enums.ts";

// ---------------------------------------------------------------------------
// The WhatsApp group sync tool (migration 0033; strategy repo:
// teamna/strategy/whatsapp-group-sync.md). Owner and admins only.
// ---------------------------------------------------------------------------

/** One WhatsApp group the business synced. */
export const syncGroupSchema = z.object({
  id: uuidSchema,
  groupName: z.string().nullable(),
  /** People in the group at the last sync, the Teamna number not counted. */
  memberCount: z.number().int().nullable(),
  status: groupLinkStatusSchema,
  /** The Teamna number stays in the group: "Sync now" works. */
  stay: z.boolean(),
  lastSyncedAt: isoDateTimeSchema.nullable(),
  /** When the Teamna number left the group. A new sync needs a new add. */
  leftAt: isoDateTimeSchema.nullable(),
});
export type SyncGroup = z.infer<typeof syncGroupSchema>;

/** `GET /v1/business/businesses/:businessId/whatsapp-sync`: everything the sheet opens with. */
export const whatsAppSyncSchema = z.object({
  mode: groupImportModeSchema,
  teamnaNumber: z.string(),
  /** The proved numbers of the owners and admins, masked: the person who adds the number must be a group admin on one. */
  adminPhonesMasked: z.array(z.string()),
  /** The same numbers in full (E.164). The sync tool shows them: they are the owner's own numbers. */
  adminPhones: z.array(z.string()),
  groups: z.array(syncGroupSchema),
  /** The connect the owner started and that no group reached yet. */
  waiting: groupLinkSchema.nullable(),
  /**
   * The work in each step: people to sort, parents whose swimmers the owner did not link yet
   * (step 2, "Link swimmers"), people who left every synced group.
   */
  counts: z.object({ unsorted: z.number().int(), families: z.number().int(), left: z.number().int() }),
});
export type WhatsAppSync = z.infer<typeof whatsAppSyncSchema>;

export const syncPeopleViewSchema = z.enum(["unsorted", "left", "sorted"]);
export type SyncPeopleView = z.infer<typeof syncPeopleViewSchema>;

export const syncPeopleQuerySchema = z.object({ view: syncPeopleViewSchema.default("unsorted") });

/** One person from a synced group. */
export const syncPersonSchema = z.object({
  id: uuidSchema,
  /** The name Teamna uses: the owner's, else WhatsApp's, else the full number (`formatPhone`). */
  name: z.string(),
  whatsappName: z.string().nullable(),
  phoneE164: z.string(),
  phoneMasked: z.string(),
  /** The profile photo of a sorted person (never a WhatsApp photo). Signed, expires in 12 to 24 hours. */
  photoUrl: z.string().nullable(),
  classification: importClassificationSchema,
  roles: z.array(syncRoleSchema),
  sortedAt: isoDateTimeSchema.nullable(),
  /** In none of the synced groups any more. */
  leftGroupAt: isoDateTimeSchema.nullable(),
  /** The synced groups the person is in now, by name. */
  groups: z.array(z.string()),
  /** The owner kept the person after they left every synced group. Change can still archive them. */
  keptAt: isoDateTimeSchema.nullable(),
  /** The owner archived the person from the tool, and Keep can bring back what the archive took. */
  canRestore: z.boolean(),
});
export type SyncPerson = z.infer<typeof syncPersonSchema>;

export const syncPeopleResponseSchema = z.object({ items: z.array(syncPersonSchema) });
export type SyncPeopleResponse = z.infer<typeof syncPeopleResponseSchema>;

/**
 * `POST .../whatsapp-sync/people/:id/sort`: Skip, or one or more roles. `children` only with
 * `parent`. `name` replaces the WhatsApp name (and a later sync keeps it).
 */
export const sortSyncPersonRequestSchema = z
  .object({
    skip: z.boolean().optional(),
    roles: z.array(syncRoleSchema).max(3).default([]),
    name: z.string().trim().min(1).max(120).optional(),
    children: z.array(newFamilyChildSchema).max(10).default([]),
  })
  .refine((body) => Boolean(body.skip) !== body.roles.length > 0, "Choose Skip or at least one role")
  .refine((body) => new Set(body.roles).size === body.roles.length, "Each role once")
  .refine((body) => body.children.length === 0 || body.roles.includes("parent"), "Children need the parent role");
export type SortSyncPersonRequest = z.infer<typeof sortSyncPersonRequestSchema>;

/**
 * `PUT .../whatsapp-sync/people/:id/roles`: the roles a person the tool sorted has now, for Change.
 * A role that is new is made as Save makes it. A role that is gone is taken away the way Archive
 * takes it (refused while that role has sessions booked). No role is Skip. A skipped person with
 * roles is sorted again.
 */
export const setSyncRolesRequestSchema = z
  .object({
    roles: z.array(syncRoleSchema).max(3),
    name: z.string().trim().min(1).max(120).optional(),
  })
  .refine((body) => new Set(body.roles).size === body.roles.length, "Each role once");
export type SetSyncRolesRequest = z.infer<typeof setSyncRolesRequestSchema>;

/** A swimmer at the business that a parent can be linked to (step 2 of the sort). */
export const syncSwimmerSchema = z.object({
  participantId: uuidSchema,
  name: z.string(),
  age: z.number().int().nullable(),
  /** The family the swimmer belongs to, and the name of its first adult ("Maryam Al Hashimi"). */
  familyUserId: uuidSchema,
  familyName: z.string(),
});
export type SyncSwimmer = z.infer<typeof syncSwimmerSchema>;

/** A parent from the sync, with the swimmers linked to them now. */
export const syncFamilySchema = z.object({
  person: syncPersonSchema,
  swimmers: z.array(syncSwimmerSchema),
  /** The owner has not linked the swimmers yet (Done) or chosen Later. */
  pending: z.boolean(),
});
export type SyncFamily = z.infer<typeof syncFamilySchema>;

/** `GET .../whatsapp-sync/families`: the parents still to link, and every swimmer they can be linked to. */
export const syncFamiliesResponseSchema = z.object({
  items: z.array(syncFamilySchema),
  swimmers: z.array(syncSwimmerSchema),
});
export type SyncFamiliesResponse = z.infer<typeof syncFamiliesResponseSchema>;

/**
 * `POST .../whatsapp-sync/families/:id/swimmers`: link swimmers to a parent from the sync, then the
 * parent is done. `participantIds` are swimmers already at the business: one in another family
 * makes this parent a second adult of that family. `children` are new swimmers in this parent's
 * family. `later` marks the parent done with no more swimmers for now, and comes with no swimmers.
 */
export const linkSwimmersRequestSchema = z
  .object({
    participantIds: z.array(uuidSchema).max(20).default([]),
    children: z.array(newFamilyChildSchema).max(10).default([]),
    later: z.boolean().optional(),
  })
  // Later is "no swimmers for now": it never links or adds one (internal review F1.2).
  .refine((body) => !body.later || (body.participantIds.length === 0 && body.children.length === 0), "Later takes no swimmers");
export type LinkSwimmersRequest = z.infer<typeof linkSwimmersRequestSchema>;

/** What a sync changed, for the line "12 new, 3 updated, 1 left the group". */
export const syncResultSchema = z.object({
  added: z.number().int(),
  updated: z.number().int(),
  left: z.number().int(),
  /** People WhatsApp hides the number of (a WhatsApp LID). Not imported. */
  hidden: z.number().int(),
});
export type SyncResult = z.infer<typeof syncResultSchema>;

/**
 * The group invite link the owner pastes (owner decision 2026-10-10): the link
 * (`https://chat.whatsapp.com/<code>`), the text WhatsApp shares with it, or the code alone. The
 * server reads the code out of it.
 */
export const inviteLinkRequestSchema = z.object({ link: z.string().trim().min(1).max(500) });
export type InviteLinkRequest = z.infer<typeof inviteLinkRequestSchema>;

/**
 * `POST .../group-links/:id/invite/check`: the group behind the link, read without joining it.
 * `ok`: Teamna can join (an owner or admin of this business is a group admin, or WhatsApp shows no
 * admin id). `not_admin`: no owner or admin of this business is a group admin. `synced_already`:
 * Teamna is in this group for this business now. `invalid`: WhatsApp does not know the link.
 */
export const inviteLinkCheckSchema = z.object({
  result: z.enum(["ok", "not_admin", "synced_already", "invalid"]),
  groupName: z.string().nullable(),
  /** Everyone in the group, as WhatsApp counts them. Null when the link does not work. */
  memberCount: z.number().int().nullable(),
  /** True when WhatsApp shows an owner or admin of this business as a group admin (false when it hides every admin id). */
  adminSeen: z.boolean(),
});
export type InviteLinkCheck = z.infer<typeof inviteLinkCheckSchema>;

/**
 * `POST .../group-links/:id/invite/join`. `synced`: Teamna joined and synced; `sync` is null when the
 * join event synced the group first. `approval_needed`: Teamna is not in the group yet, most likely
 * because a group admin must approve new members. The link keeps waiting, and the join event syncs
 * it after the approval.
 */
export const inviteLinkJoinSchema = z.object({
  result: z.enum(["synced", "approval_needed"]),
  groupName: z.string().nullable(),
  sync: syncResultSchema.nullable(),
});
export type InviteLinkJoin = z.infer<typeof inviteLinkJoinSchema>;

/** What archiving a person who left would take away, shown before the owner confirms. */
export const syncArchivePreviewSchema = z.object({
  roles: z.array(syncRoleSchema),
  upcomingBookings: z.number().int(),
  sessionsLeft: z.number().int(),
});
export type SyncArchivePreview = z.infer<typeof syncArchivePreviewSchema>;
